import { jsPDF } from 'jspdf';

function safeFilename(s) {
  return (s || 'recap').replace(/[^a-z0-9-_]+/gi, '_').slice(0, 60);
}

function todayStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function exportRecapAsMarkdown(summary, flashcards, paper) {
  const title = paper?.name || 'Paper';
  const lines = [
    `# Session Recap — ${title}`,
    '',
    `*Exported ${todayStamp()}*`,
    '',
    '## Summary',
    '',
    summary || '_No summary available._',
    '',
    '## Flashcards',
    '',
  ];
  for (let i = 0; i < flashcards.length; i++) {
    const { q, a } = flashcards[i];
    lines.push(`### Card ${i + 1}`, '', `**Q:** ${q}`, '', `**A:** ${a}`, '', '---', '');
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8' });
  downloadBlob(blob, `${safeFilename(title)}_recap_${todayStamp()}.md`);
}

export function exportRecapAsAnkiTSV(flashcards, paper) {
  // Standard Anki plain-text import format: question<TAB>answer, one card per line
  const lines = flashcards.map(({ q, a }) => `${q}\t${a}`);
  const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
  downloadBlob(blob, `${safeFilename(paper?.name)}_flashcards_${todayStamp()}.txt`);
}

export function exportRecapAsPDF(summary, flashcards, paper) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) { doc.addPage(); y = margin; }
  };

  const write = (text, fontSize, opts = {}) => {
    doc.setFontSize(fontSize);
    doc.setFont(undefined, opts.bold ? 'bold' : opts.italic ? 'italic' : 'normal');
    doc.setTextColor(...(opts.color || [20, 20, 20]));
    const lines = doc.splitTextToSize(text, usableWidth - (opts.indent || 0));
    const lh = fontSize * 1.35;
    for (const line of lines) {
      ensureSpace(lh);
      doc.text(line, margin + (opts.indent || 0), y);
      y += lh;
    }
  };

  write(`Session Recap — ${paper?.name || 'Paper'}`, 18, { bold: true });
  y += 4;
  write(`Exported ${todayStamp()}`, 10, { italic: true, color: [120, 120, 120] });
  y += 14;

  write('Summary', 14, { bold: true, color: [30, 64, 175] });
  y += 4;
  // Strip markdown bullets for PDF
  const summaryLines = (summary || 'No summary.').split('\n').map(l => l.replace(/^[-*]\s+/, '• '));
  for (const line of summaryLines) {
    if (line.trim()) write(line.trim(), 11);
  }
  y += 14;

  write('Flashcards', 14, { bold: true, color: [30, 64, 175] });
  y += 8;
  for (let i = 0; i < flashcards.length; i++) {
    const { q, a } = flashcards[i];
    ensureSpace(60);
    write(`Q${i + 1}: ${q}`, 11, { bold: true });
    write(`A: ${a}`, 11, { indent: 12, color: [60, 60, 60] });
    y += 8;
  }

  doc.save(`${safeFilename(paper?.name)}_recap_${todayStamp()}.pdf`);
}

export async function copyRecapToClipboard(summary, flashcards, paper) {
  const title = paper?.name || 'Paper';
  const lines = [`Session Recap — ${title}`, `Exported ${todayStamp()}`, '', 'SUMMARY', ''];
  const summaryLines = (summary || '').split('\n').map(l => l.replace(/^[-*]\s+/, '• '));
  lines.push(...summaryLines, '', 'FLASHCARDS', '');
  for (let i = 0; i < flashcards.length; i++) {
    const { q, a } = flashcards[i];
    lines.push(`Q${i + 1}: ${q}`, `A: ${a}`, '');
  }
  await navigator.clipboard.writeText(lines.join('\n'));
}
